import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, Tooltip, Button, useTheme, alpha } from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTranslation } from 'react-i18next';
import { GROUPS, STATES, normalizeVersatility, nextState, emptyVersatility, hasAnyValue } from '../../utils/versatility';
import GraduatedScale from './GraduatedScale';

/**
 * Click-to-cycle editor: 3 groups × 4 cells.
 *
 * Input shortcuts:
 *   - Click cell      : cycles that cell (null → cant → switch → primary → null).
 *   - Drag from cell  : paints all hovered cells with the same state as the first click.
 *   - Click group band: applies one cycle step to ALL cells of the group at once;
 *                       if cells are mixed, unifies the group to "primary" (most useful default).
 */
const DefensiveVersatilityEditor = ({
  value,
  onChange,
  showTitle = true,
  showLegend = true,
  showReset = true
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const v = normalizeVersatility(value);

  const cellSize = 30;
  const cellGap = 3;
  const groupGap = 12;
  const groupCellsWidth = 4 * cellSize + 3 * cellGap;

  const colors = {
    [STATES.PRIMARY]: theme.palette.success.main,
    [STATES.SWITCH]: '#fbc02d',
    [STATES.CANT]: theme.palette.error.main,
    none: alpha(theme.palette.grey[400], 0.2)
  };

  const cellColor = (state) => {
    if (state === STATES.PRIMARY) return colors[STATES.PRIMARY];
    if (state === STATES.SWITCH) return colors[STATES.SWITCH];
    if (state === STATES.CANT) return colors[STATES.CANT];
    return colors.none;
  };

  const stateTooltip = (state) => {
    if (state === STATES.PRIMARY) return t('defensiveVersatility.legend.primary');
    if (state === STATES.SWITCH) return t('defensiveVersatility.legend.switch');
    if (state === STATES.CANT) return t('defensiveVersatility.legend.cant');
    return t('defensiveVersatility.legend.none');
  };

  // --- Drag state ---
  // While dragging, dragPaintState holds the state we paint onto every hovered cell.
  // liveValueRef mirrors the current value during a drag (props get stale between cell updates).
  // paintedRef prevents re-painting the same cell within the same drag gesture.
  const [dragPaintState, setDragPaintState] = useState(undefined);
  const liveValueRef = useRef(v);
  const paintedRef = useRef(new Set());

  useEffect(() => {
    if (dragPaintState === undefined) {
      liveValueRef.current = v;
    }
  }, [v, dragPaintState]);

  // Global mouseup/touchend to terminate a drag started on any cell
  useEffect(() => {
    if (dragPaintState === undefined) return undefined;
    const end = () => {
      setDragPaintState(undefined);
      paintedRef.current = new Set();
    };
    window.addEventListener('mouseup', end);
    window.addEventListener('touchend', end);
    window.addEventListener('touchcancel', end);
    return () => {
      window.removeEventListener('mouseup', end);
      window.removeEventListener('touchend', end);
      window.removeEventListener('touchcancel', end);
    };
  }, [dragPaintState]);

  const applyState = useCallback((pos, state) => {
    liveValueRef.current = { ...liveValueRef.current, [pos]: state };
    onChange(liveValueRef.current);
  }, [onChange]);

  const applyStateToGroup = useCallback((group, state) => {
    const updated = { ...liveValueRef.current };
    group.positions.forEach(p => { updated[p] = state; });
    liveValueRef.current = updated;
    onChange(updated);
  }, [onChange]);

  const handleCellPointerDown = useCallback((pos, e) => {
    e.preventDefault();
    const newState = nextState(liveValueRef.current[pos]);
    paintedRef.current = new Set([pos]);
    setDragPaintState(newState);
    applyState(pos, newState);
  }, [applyState]);

  const handleCellMouseEnter = useCallback((pos) => {
    if (dragPaintState === undefined) return;
    if (paintedRef.current.has(pos)) return;
    paintedRef.current.add(pos);
    applyState(pos, dragPaintState);
  }, [dragPaintState, applyState]);

  // Touch drag: detect cells under the finger via elementFromPoint
  const handleTouchMove = useCallback((e) => {
    if (dragPaintState === undefined) return;
    const touch = e.touches[0];
    if (!touch) return;
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!el) return;
    const cellEl = el.closest('[data-versatility-cell]');
    if (!cellEl) return;
    const pos = cellEl.getAttribute('data-versatility-cell');
    if (!pos || paintedRef.current.has(pos)) return;
    paintedRef.current.add(pos);
    applyState(pos, dragPaintState);
  }, [dragPaintState, applyState]);

  const handleBandClick = useCallback((group) => {
    const states = group.positions.map(p => liveValueRef.current[p] ?? null);
    const allSame = states.every(s => s === states[0]);
    const targetState = allSame ? nextState(states[0]) : STATES.PRIMARY;
    applyStateToGroup(group, targetState);
  }, [applyStateToGroup]);

  const handleReset = () => onChange(emptyVersatility());

  return (
    <Box
      onTouchMove={handleTouchMove}
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        gap: 1,
        userSelect: 'none',
        touchAction: dragPaintState === undefined ? 'auto' : 'none'
      }}
    >
      {showTitle && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ShieldIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="subtitle2" fontWeight={700}>
              {t('defensiveVersatility.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('defensiveVersatility.cycleHint')}
            </Typography>
          </Box>
          {showReset && hasAnyValue(v) && (
            <Button
              size="small"
              startIcon={<RestartAltIcon fontSize="small" />}
              onClick={handleReset}
              sx={{ textTransform: 'none', fontSize: '0.7rem' }}
            >
              {t('common.reset')}
            </Button>
          )}
        </Box>
      )}

      {/* Group bands (clickable: applies one cycle step to the whole group) */}
      <Box sx={{ display: 'flex', gap: `${groupGap}px` }}>
        {GROUPS.map(group => (
          <Tooltip key={group.key} title={t('defensiveVersatility.bandHint')} arrow>
            <Box
              onClick={() => handleBandClick(group)}
              sx={{
                width: `${groupCellsWidth}px`,
                py: 0.5,
                borderRadius: 0.75,
                backgroundColor: alpha(theme.palette.text.primary, 0.85),
                color: theme.palette.background.paper,
                textAlign: 'center',
                boxSizing: 'border-box',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: theme.palette.text.primary,
                  transform: 'translateY(-1px)',
                  boxShadow: theme.shadows[2]
                }
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.4, lineHeight: 1 }}>
                {t(group.labelKey)}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>

      {/* Numeric ruler */}
      <GraduatedScale cellSize={cellSize} cellGap={cellGap} groupGap={groupGap} />

      {/* Cells (click + drag-paint) */}
      <Box sx={{ display: 'flex', gap: `${groupGap}px` }}>
        {GROUPS.map(group => (
          <Box key={group.key} sx={{ display: 'flex', gap: `${cellGap}px` }}>
            {group.positions.map(p => {
              const state = v[p];
              return (
                <Tooltip key={p} title={stateTooltip(state)} arrow>
                  <Box
                    role="button"
                    data-versatility-cell={p}
                    onMouseDown={(e) => handleCellPointerDown(p, e)}
                    onMouseEnter={() => handleCellMouseEnter(p)}
                    onTouchStart={(e) => handleCellPointerDown(p, e)}
                    sx={{
                      width: cellSize,
                      height: cellSize,
                      borderRadius: 0.75,
                      backgroundColor: cellColor(state),
                      border: state ? 'none' : `1px solid ${alpha(theme.palette.grey[400], 0.4)}`,
                      boxSizing: 'border-box',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: theme.shadows[2]
                      }
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        ))}
      </Box>

      {showLegend && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: colors[STATES.PRIMARY] }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              {t('defensiveVersatility.legend.primary')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: colors[STATES.SWITCH] }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              {t('defensiveVersatility.legend.switch')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: colors[STATES.CANT] }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              {t('defensiveVersatility.legend.cant')}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DefensiveVersatilityEditor;
